/**
 * Shopping Preferences Hook
 *
 * Lightweight hook that consumes the ShoppingPreferencesContext.
 * Provides easy access to shopping preferences state and actions.
 *
 * This hook is a simple wrapper around the context, keeping the API
 * familiar while the heavy lifting is done in the context provider.
 */

import { useContextSelector } from 'use-context-selector';
import { ShoppingPreferencesContext } from '@context/shopping-preferences.context';
import { UseShoppingPreferencesReturn } from '@lib/shopping-preferences/types';

/**
 * Main shopping preferences hook
 * Consumes the ShoppingPreferencesContext
 */
export const useShoppingPreferences = (): UseShoppingPreferencesReturn => {
  const preferences = useContextSelector(ShoppingPreferencesContext, (v) => v.preferences);
  const sessionPrefs = useContextSelector(ShoppingPreferencesContext, (v) => v.sessionPrefs);
  const updatePreferences = useContextSelector(ShoppingPreferencesContext, (v) => v.updatePreferences);
  const setSessionPreferences = useContextSelector(ShoppingPreferencesContext, (v) => v.setSessionPreferences);
  const clearPreferences = useContextSelector(ShoppingPreferencesContext, (v) => v.clearPreferences);
  const source = useContextSelector(ShoppingPreferencesContext, (v) => v.source);
  const isLoading = useContextSelector(ShoppingPreferencesContext, (v) => v.isLoading);
  const isError = useContextSelector(ShoppingPreferencesContext, (v) => v.isError);
  const isEnabled = useContextSelector(ShoppingPreferencesContext, (v) => v.isEnabled);
  const hasUserSelectedPrefs = useContextSelector(ShoppingPreferencesContext, (v) => v.hasUserSelectedPrefs);

  return {
    preferences,
    sessionPrefs,
    updatePreferences,
    setSessionPreferences,
    clearPreferences,
    source,
    isLoading,
    isError,
    isEnabled,
    hasUserSelectedPrefs,
  };
};

// Re-export constants for backward compatibility
export { WOMEN_UNIVERSE, MEN_UNIVERSE } from '@lib/shopping-preferences/types';
export type { StorageSource } from '@lib/shopping-preferences/types';

export default useShoppingPreferences;
